import { useRef, useState } from "react";
import { toast } from "sonner";
import { Download, Eye, Link2, Link2Off, Paperclip, Trash, Upload } from "lucide-react";
import { errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { bytes } from "@/features/library/format";
import { cn } from "@/lib/utils";
import { copyFileLink, downloadFile } from "./download";
import { FileName } from "./FileName";
import { canPreview, FilePreviewDialog } from "./FilePreviewDialog";
import { FilePickerDialog } from "./FilePickerDialog";
import { uploadFiles } from "./upload";
import { useFileMutations, useInvalidateFiles, useTargetFiles } from "./useFiles";
import type { FileRow, FileTargetType } from "./types";

/** Files attached to (owned by) or linked to one note/record, task, manuscript or paper. */
export function FileLinksPanel(props: { targetType: FileTargetType; targetId: string; workspaceId: string; readOnly?: boolean; hint?: string; className?: string }) {
  const files = useTargetFiles(props.targetType, props.targetId);
  const m = useFileMutations();
  const invalidate = useInvalidateFiles();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [picking, setPicking] = useState(false);
  const [preview, setPreview] = useState<FileRow | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<FileRow | null>(null);

  const isOwned = (f: FileRow) => f.owner.type === props.targetType && f.owner.id === props.targetId;
  const linkOf = (f: FileRow) => f.links.find((l) => l.targetType === props.targetType && l.targetId === props.targetId);

  const upload = async (list: File[]) => {
    if (!list.length || props.readOnly) return;
    setBusy(true);
    try {
      await uploadFiles(props.workspaceId, list, { linkType: props.targetType, linkId: props.targetId });
      toast.success(list.length === 1 ? "File added." : `${list.length} files added.`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = "";
      void invalidate();
    }
  };

  const unlink = async (f: FileRow) => {
    const link = linkOf(f);
    if (!link) return;
    try {
      await m.unlink.mutateAsync({ fileId: f.id, linkId: link.id });
      toast.success("Link removed. The file stays in the Files area.");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const list = files.data ?? [];
  return (
    <div
      className={cn("space-y-2 rounded-md text-sm", dragging && "outline-2 outline-dashed outline-primary", props.className)}
      onDragOver={(e) => {
        if (props.readOnly || !e.dataTransfer.types.includes("Files")) return;
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        if (props.readOnly) return;
        e.preventDefault();
        setDragging(false);
        void upload(Array.from(e.dataTransfer.files));
      }}
      data-testid={`files-panel-${props.targetType}`}
    >
      {props.hint && <p className="text-xs text-muted-foreground">{props.hint}</p>}
      <ul className="space-y-1" aria-label="Files">
        {list.map((f) => {
          const owned = isOwned(f);
          return (
            <li key={f.id} className="flex items-center gap-1.5 rounded border bg-background px-2 py-1">
              <span className="min-w-0 flex-1">
                <button
                  type="button"
                  className="flex max-w-full text-left hover:underline"
                  onClick={() => (canPreview(f) ? setPreview(f) : downloadFile(f))}
                  title={f.encrypted ? "Encrypted file" : f.filename}
                >
                  <FileName file={f} />
                </button>
                <span className="block truncate text-xs text-muted-foreground">
                  {bytes(f.sizeBytes)} · {owned ? "attached here" : "linked"}
                  {f.description ? ` · ${f.description}` : ""}
                </span>
              </span>
              {canPreview(f) && (
                <button type="button" aria-label={`Preview ${f.filename}`} className="rounded p-1 hover:bg-accent" onClick={() => setPreview(f)}>
                  <Eye className="size-4" />
                </button>
              )}
              <button type="button" aria-label={`Download ${f.filename}`} className="rounded p-1 hover:bg-accent" onClick={() => downloadFile(f)}>
                <Download className="size-4" />
              </button>
              <button type="button" aria-label={`Copy link to ${f.filename}`} className="rounded p-1 hover:bg-accent" onClick={() => copyFileLink(f)}>
                <Link2 className="size-4" />
              </button>
              {!props.readOnly &&
                (owned ? (
                  <button type="button" aria-label={`Remove ${f.filename}`} className="rounded p-1 hover:bg-accent" onClick={() => setConfirmDelete(f)}>
                    <Trash className="size-4" />
                  </button>
                ) : (
                  <button type="button" aria-label={`Unlink ${f.filename}`} className="rounded p-1 hover:bg-accent" onClick={() => unlink(f)}>
                    <Link2Off className="size-4" />
                  </button>
                ))}
            </li>
          );
        })}
        {files.data && !list.length && <li className="text-muted-foreground">No files yet.{props.readOnly ? "" : " Drop files here or use the buttons below."}</li>}
        {files.isLoading && <li className="text-muted-foreground">Loading files…</li>}
        {files.isError && <li className="text-destructive">{errorMessage(files.error)}</li>}
      </ul>
      {!props.readOnly && (
        <div className="flex flex-wrap gap-1.5">
          <input ref={input} type="file" multiple className="sr-only" aria-label="Choose files to upload" tabIndex={-1} onChange={(e) => upload(Array.from(e.target.files ?? []))} />
          <Button type="button" size="xs" variant="outline" disabled={busy} onClick={() => input.current?.click()}>
            <Upload /> {busy ? "Uploading…" : "Upload files…"}
          </Button>
          <Button type="button" size="xs" variant="outline" onClick={() => setPicking(true)}>
            <Paperclip /> Link existing file…
          </Button>
        </div>
      )}
      {picking && (
        <FilePickerDialog
          workspaceId={props.workspaceId}
          excludeIds={list.map((f) => f.id)}
          onOpenChange={setPicking}
          onPick={async (f) => {
            setPicking(false);
            try {
              await m.link.mutateAsync({ fileId: f.id, targetType: props.targetType, targetId: props.targetId });
              toast.success("File linked.");
            } catch (e) {
              toast.error(errorMessage(e));
            }
          }}
        />
      )}
      {preview && <FilePreviewDialog file={preview} onClose={() => setPreview(null)} />}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this file?</AlertDialogTitle>
            <AlertDialogDescription>
              The file is removed from this item and from the Files area, including any links to it. Copies stored with manuscript submissions are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                const f = confirmDelete;
                setConfirmDelete(null);
                if (!f) return;
                try {
                  await m.remove.mutateAsync(f.id);
                } catch (e) {
                  toast.error(errorMessage(e));
                }
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
